import { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, TrendingUp, Sparkles, ChevronRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import AppDownloadModal from './AppDownloadModal';

export default function DispoEscape() {
  const { t } = useLanguage();
  const [isAppModalOpen, setIsAppModalOpen] = useState(false);

  return (
    <section id="dispo-escape" className="relative bg-black py-12 lg:py-16 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 border border-red-500/20 rounded-full mb-6"
          >
            <AlertCircle className="h-5 w-5 text-red-400" />
            <span className="text-red-400 font-semibold text-sm">
              {t('showcase.dispo.badge')}
            </span>
          </motion.div>

          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            {t('showcase.dispo.callout.title')}
          </h2>
        </motion.div>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="relative max-w-5xl mx-auto"
        >
          {/* Outer glow */}
          <div className="absolute -inset-1 bg-gradient-to-r from-red-600 via-orange-600 to-red-600 rounded-3xl blur-xl opacity-30 group-hover:opacity-40 transition-opacity" />
          
          {/* Main content card */}
          <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border-2 border-red-500/40 rounded-3xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-3 gap-8 items-center">
              {/* Left: Icon */}
              <motion.div
                whileHover={{ scale: 1.05, rotate: 5 }}
                className="flex justify-center lg:justify-start"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl blur-lg opacity-50" />
                  <div className="relative w-24 h-24 lg:w-32 lg:h-32 bg-gradient-to-br from-red-500/20 to-orange-500/20 border-2 border-red-400/40 rounded-2xl flex items-center justify-center">
                    <AlertCircle className="h-12 w-12 lg:h-16 lg:w-16 text-red-400" />
                  </div>
                </div>
              </motion.div>

              {/* Center: Message */}
              <div className="lg:col-span-2">
                <p className="text-gray-200 text-lg lg:text-xl leading-relaxed mb-6">
                  {t('showcase.dispo.callout.text')}
                </p>

                {/* Features Grid */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 }}
                    className="flex items-center gap-3 p-3 bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl"
                  >
                    <TrendingUp className="h-6 w-6 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">AI-powered spending analysis</span>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6 }}
                    className="flex items-center gap-3 p-3 bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl"
                  >
                    <Sparkles className="h-6 w-6 text-purple-400 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">Personalized escape strategies</span>
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Bottom CTA Strip */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.7 }}
              className="mt-8 pt-6 border-t border-gray-700/50"
            >
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-gray-400 text-sm">
                  Join thousands breaking free from the Dispo cycle
                </p>
                <button 
                  onClick={() => setIsAppModalOpen(true)}
                  className="group flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-semibold rounded-xl transition-all hover:scale-105"
                >
                  {t('dispoEscape.cta')}
                  <ChevronRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>
            </motion.div>

            {/* Decorative corner accents */}
            <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-br from-red-400/10 to-transparent rounded-full blur-2xl" />
            <div className="absolute bottom-4 left-4 w-24 h-24 bg-gradient-to-tr from-orange-400/10 to-transparent rounded-full blur-2xl" />
          </div>
        </motion.div>

        {/* Bottom stats or badges */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="mt-12 flex flex-wrap justify-center gap-8 text-center"
        >
          <div>
            <div className="text-3xl font-bold text-white mb-1">89%</div>
            <div className="text-gray-400 text-sm">Success Rate</div>
          </div>
          <div className="w-px h-12 bg-gray-700" />
          <div>
            <div className="text-3xl font-bold text-white mb-1">€2,450</div>
            <div className="text-gray-400 text-sm">Avg. Savings</div>
          </div>
          <div className="w-px h-12 bg-gray-700" />
          <div>
            <div className="text-3xl font-bold text-white mb-1">3 Months</div>
            <div className="text-gray-400 text-sm">Avg. Escape Time</div>
          </div>
        </motion.div>
      </div>

      {/* App Download Modal */}
      <AppDownloadModal isOpen={isAppModalOpen} onClose={() => setIsAppModalOpen(false)} />
    </section>
  );
}
